import type { TabNavItem } from '@mercedes-benz/odp-theme-v2';
import {
  DocumentIcon,
  RelationsIcon,
  WrenchIcon,
} from '@mercedes-benz/odp-theme-v2';
import {
  RELATION_CONSUMES_API,
  RELATION_DEPENDS_ON,
  RELATION_PROVIDES_API,
} from '@backstage/catalog-model';
import { useEntity } from '@backstage/plugin-catalog-react';
import { hasAemPluginAnnotation } from '@mercedes-benz/plugin-seamless';
import ApiIcon from '@mui/icons-material/Api';
import BuildIcon from '@mui/icons-material/Build';
import CodeIcon from '@mui/icons-material/Code';
import DependenciesIcon from '@mui/icons-material/AccountTree';
import FeedbackIcon from '@mui/icons-material/Feedback';
import InfoIcon from '@mui/icons-material/Info';
import InsightsIcon from '@mui/icons-material/Insights';
import MergeTypeIcon from '@mui/icons-material/MergeType';
import SecurityIcon from '@mui/icons-material/Security';
import SettingsIcon from '@mui/icons-material/Settings';
import ShieldIcon from '@mui/icons-material/Shield';
import StorageIcon from '@mui/icons-material/Storage';
import { AemManifestTab } from './AemManifestTab';
import { AppStandardInsightsTab } from './AppStandardInsightsTab';
import { BlackDuckTab } from './BlackDuckTab';
import { CICDTab } from './CICDTab';
import { CodeInsightsTab } from './CodeInsightsTab';
import { CoreDetailsTab } from '../system-details/CoreDetailsTab';
import { DependabotTab } from './DependabotTab';
import { DocsTab } from './DocsTab';
import { EntityPageLayout } from './EntityPageLayout';
import { FeedbackTab } from './FeedbackTab';
import { HarborTab } from './HarborTab';
import { PullRequestsTab } from './PullRequestsTab';
import { RelatedEntitiesTab } from './RelatedEntitiesTab';
import { RelationsTab } from '../system-details/RelationsTab';
import { SonarQubeTab } from './SonarQubeTab';
import { TechInsightsTab } from './TechInsightsTab';

const BASE_TABS: TabNavItem[] = [
  { id: 'overview', label: 'Overview', icon: InfoIcon },
  { id: 'insights', label: 'Insights', icon: WrenchIcon },
  {
    id: 'app-standard-insights',
    label: 'AppStandardInsights',
    icon: InsightsIcon,
  },
  { id: 'ci-cd', label: 'CI/CD', icon: BuildIcon },
  { id: 'blackduck', label: 'BlackDuck', icon: SecurityIcon },
  { id: 'sonarqube', label: 'SonarQube', icon: InsightsIcon },
  { id: 'harbor', label: 'Harbor', icon: StorageIcon },
  { id: 'dependencies', label: 'Dependencies', icon: DependenciesIcon },
  { id: 'provided-apis', label: 'API', icon: ApiIcon },
  { id: 'consumed-apis', label: 'Consumed APIs', icon: ApiIcon },
  { id: 'docs', label: 'Docs', icon: DocumentIcon },
  { id: 'pull-requests', label: 'Pull Requests', icon: MergeTypeIcon },
  { id: 'code-insights', label: 'Code Insights', icon: CodeIcon },
  { id: 'dependabot', label: 'Dependabot', icon: ShieldIcon },
];

const AEM_TAB: TabNavItem = {
  id: 'aem-manifest',
  label: 'AEM Configuration',
  icon: SettingsIcon,
};

const TAIL_TABS: TabNavItem[] = [
  { id: 'feedback', label: 'Feedback', icon: FeedbackIcon },
  { id: 'relations', label: 'Relations', icon: RelationsIcon },
];

export const ComponentEntityPage = () => {
  const { entity } = useEntity();
  const showAem = hasAemPluginAnnotation(entity);

  const tabs: TabNavItem[] = showAem
    ? [...BASE_TABS, AEM_TAB, ...TAIL_TABS]
    : [...BASE_TABS, ...TAIL_TABS];

  return (
    <EntityPageLayout tabs={tabs}>
      {activeTab => (
        <>
          {activeTab === 'overview' && <CoreDetailsTab />}
          {activeTab === 'ci-cd' && <CICDTab />}
          {activeTab === 'sonarqube' && <SonarQubeTab />}
          {activeTab === 'blackduck' && <BlackDuckTab />}
          {activeTab === 'harbor' && <HarborTab />}
          {activeTab === 'provided-apis' && (
            <RelatedEntitiesTab
              relationType={RELATION_PROVIDES_API}
              kindFilter="api"
              title="Provided APIs"
            />
          )}
          {activeTab === 'consumed-apis' && (
            <RelatedEntitiesTab
              relationType={RELATION_CONSUMES_API}
              kindFilter="api"
              title="Consumed APIs"
            />
          )}
          {activeTab === 'dependencies' && (
            <RelatedEntitiesTab
              relationType={RELATION_DEPENDS_ON}
              title="Dependencies"
            />
          )}
          {activeTab === 'docs' && <DocsTab />}
          {activeTab === 'pull-requests' && <PullRequestsTab />}
          {activeTab === 'code-insights' && <CodeInsightsTab />}
          {activeTab === 'dependabot' && <DependabotTab />}
          {activeTab === 'insights' && <TechInsightsTab />}
          {activeTab === 'app-standard-insights' && <AppStandardInsightsTab />}
          {activeTab === 'aem-manifest' && <AemManifestTab />}
          {activeTab === 'feedback' && <FeedbackTab />}
          {activeTab === 'relations' && <RelationsTab />}
        </>
      )}
    </EntityPageLayout>
  );
};
